import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Loader,
  Search,
  Filter,
  Info,
  Phone,
  Calendar,
  UserCircle,
  X,
  TrendingUp,
} from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { customerService } from "../api/customerService";
import { useNotification } from "../contexts/NotificationContext";
import { formatNumberWithSeparator } from "../utils/numberFormat";
import CustomerTransactionDetailModal from "../components/CustomerTransactionDetailModal";
import CustomerNotes from "../components/CustomerNotes";

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [filterCreatedBy, setFilterCreatedBy] = useState("");
  const filterMenuRef = useRef(null);

  useEffect(() => {
    if (!filterOpen) return;
    const handleClickOutside = (e) => {
      if (
        filterMenuRef.current &&
        !filterMenuRef.current.contains(e.target)
      ) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [filterOpen]);

  // Fetch customer details and transactions
  useEffect(() => {
    fetchCustomerData();
  }, [id, currentPage]);

  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      const customerResponse = await customerService.get(id);

      setCustomerData(customerResponse);
      setTransactions(customerResponse.history_transactions || []);
    } catch (err) {
      console.error("Error fetching customer data:", err);
      showNotification("error", "Gagal memuat data pelanggan");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/pelanggan");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("id-ID", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const uniqueCreators = useMemo(() => {
    const seen = new Set();
    const list = [];
    transactions.forEach((t) => {
      if (t.created_by && !seen.has(t.created_by)) {
        seen.add(t.created_by);
        list.push(t.created_by);
      }
    });
    return list.sort();
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const startTs = filterStartDate
      ? new Date(`${filterStartDate}T00:00:00`).getTime()
      : null;
    const endTs = filterEndDate
      ? new Date(`${filterEndDate}T23:59:59`).getTime()
      : null;

    return transactions.filter((tx) => {
      if (term) {
        const haystack = `${tx.id || ""} ${tx.payment_method || ""} ${tx.created_by || ""}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      if (startTs || endTs) {
        const ts = tx.created_at ? new Date(tx.created_at).getTime() : null;
        if (!ts) return false;
        if (startTs && ts < startTs) return false;
        if (endTs && ts > endTs) return false;
      }
      return true;
    });
  }, [transactions, searchTerm, filterStartDate, filterEndDate]);

  const isFilterActive = !!filterStartDate || !!filterEndDate;

  const handleResetFilters = () => {
    setFilterStartDate("");
    setFilterEndDate("");
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!customerData) {
    return (
      <div className="flex h-screen bg-slate-50 items-center justify-center">
        <p className="text-slate-500">Data tidak ditemukan</p>
      </div>
    );
  }

  const lastTransactionDate = new Date(customerData.last_transaction);

  return (
    <div className="flex h-screen bg-slate-50 font-sans">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() =>
          setIsSidebarCollapsed((prev) => {
            const newState = !prev;
            localStorage.setItem("sidebarCollapsed", JSON.stringify(newState));
            return newState;
          })
        }
        activePage="pelanggan"
      />

      <div
        className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${isSidebarCollapsed ? "ml-16" : "ml-48"}`}>
        <Header
          isSidebarCollapsed={isSidebarCollapsed}
          onToggleSidebar={() =>
            setIsSidebarCollapsed((prev) => {
              const newState = !prev;
              localStorage.setItem(
                "sidebarCollapsed",
                JSON.stringify(newState),
              );
              return newState;
            })
          }
        />

        <main className="flex-1 p-6 overflow-x-hidden overflow-y-auto">
          {/* === Hero Header === */}
          <div className="relative bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-2xl p-4 lg:p-5 mb-5 overflow-hidden shadow-lg shadow-teal-900/10">
            <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/5 rounded-full blur-3xl" />
            <div className="absolute -bottom-16 -left-10 w-48 h-48 bg-emerald-300/10 rounded-full blur-3xl" />

            <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <button
                  type="button"
                  onClick={handleBack}
                  aria-label="Kembali"
                  title="Kembali"
                  className="w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 ring-1 ring-white/20 flex items-center justify-center text-white transition-colors flex-shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="min-w-0 flex flex-col items-start">
                  <h1 className="text-lg lg:text-xl font-bold text-white tracking-tight leading-tight break-words">
                    {customerData.name}
                  </h1>
                  {customerData.phone && (
                    <span className="mt-1 inline-flex items-center gap-1 text-xs text-teal-50 bg-white/10 px-2.5 py-0.5 rounded-full ring-1 ring-white/20">
                      <Phone className="w-3 h-3" /> {customerData.phone}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-start lg:items-end gap-1.5 text-white">
                {customerData.last_transaction && (
                  <div className="text-left lg:text-right">
                    <p className="text-[10px] font-semibold uppercase tracking-wider text-teal-100/80 leading-tight">
                      Transaksi Terakhir
                    </p>
                    <p className="text-sm lg:text-base font-bold leading-tight">
                      {lastTransactionDate.toLocaleDateString("id-ID", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                      <span className="ml-1.5 text-[11px] font-normal text-teal-50/90">
                        {lastTransactionDate.toLocaleTimeString("id-ID", {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Cards */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 p-6 mb-6 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Total Belanja */}
              <div className="bg-teal-50/60 ring-1 ring-teal-200/70 rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-700 mb-1">
                  Total Belanja
                </p>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-bold text-teal-900">
                    Rp {formatNumberWithSeparator(customerData.total_spending)}
                  </p>
                  <span className={`inline-flex items-center gap-0.5 px-2 py-1 rounded-md text-[10px] font-medium ${
                    customerData.spending_growth > 0 ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200' :
                    customerData.spending_growth < 0 ? 'bg-rose-100 text-rose-700 ring-1 ring-rose-200' :
                    'bg-slate-100 text-slate-700 ring-1 ring-slate-200'
                  }`}>
                    <TrendingUp className={`w-3 h-3 ${customerData.spending_growth < 0 ? 'rotate-180 text-rose-600' : customerData.spending_growth > 0 ? 'text-emerald-600' : 'text-slate-500'}`} />
                    {customerData.spending_growth > 0 ? '+' : ''}{customerData.spending_growth?.toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Total Transaksi */}
              <div className="bg-slate-50/60 ring-1 ring-slate-200/70 rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-1">
                  Total Transaksi
                </p>
                <div className="flex items-end gap-2">
                  <p className="text-lg font-bold text-slate-900">
                    {customerData.total_transactions}
                  </p>
                  <p className="text-sm text-slate-500 mb-1">Nota</p>
                </div>
              </div>

              {/* Rata-Rata Belanja */}
              <div className="bg-slate-50/60 ring-1 ring-slate-200/70 rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-1">
                  Rata-Rata Belanja
                </p>
                <div className="flex items-end gap-2">
                  <p className="text-lg font-bold text-slate-900">
                    Rp {formatNumberWithSeparator(customerData.average_transaction)}
                  </p>
                </div>
              </div>

              {/* Item / Qty Favorit */}
              <div className="bg-slate-50/60 ring-1 ring-slate-200/70 rounded-xl p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-1">
                  Item / Produk Favorit
                </p>
                <div className="flex items-end gap-2">
                  <p className="text-sm font-bold text-slate-900 truncate" title={customerData.favorite_item}>
                    {customerData.favorite_item || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Transactions Table Section */}
          <div className="bg-white rounded-2xl ring-1 ring-slate-200/70 shadow-sm mb-6 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-base font-semibold text-slate-900 tracking-tight">
                Riwayat Transaksi
              </h3>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Cari data . . ."
                    className="pl-10 pr-4 py-2 bg-slate-50 ring-1 ring-slate-200 border-0 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors w-64"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <div className="relative" ref={filterMenuRef}>
                  <button
                    type="button"
                    onClick={() => setFilterOpen((prev) => !prev)}
                    className={`p-2 ring-1 rounded-lg transition-colors relative ${
                      isFilterActive
                        ? "bg-teal-50 ring-teal-300 text-teal-700"
                        : "bg-white ring-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                    title="Filter">
                    <Filter className="w-5 h-5" />
                    {isFilterActive && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-teal-500 rounded-full ring-2 ring-white" />
                    )}
                  </button>

                  {filterOpen && (
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-lg ring-1 ring-slate-200/70 z-30 overflow-hidden">
                      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                        <p className="text-sm font-semibold text-slate-800">
                          Filter Riwayat Transaksi
                        </p>
                        <button
                          onClick={() => setFilterOpen(false)}
                          className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                          aria-label="Tutup filter">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="p-4 space-y-4">
                        <div>
                          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                            <Calendar className="w-3 h-3" /> Filter Periode Waktu
                          </p>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] font-medium text-slate-500 block mb-1">
                                Dari
                              </label>
                              <input
                                type="date"
                                value={filterStartDate}
                                onChange={(e) =>
                                  setFilterStartDate(e.target.value)
                                }
                                className="w-full px-2 py-1.5 text-xs bg-slate-50 ring-1 ring-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-medium text-slate-500 block mb-1">
                                Sampai
                              </label>
                              <input
                                type="date"
                                value={filterEndDate}
                                onChange={(e) =>
                                  setFilterEndDate(e.target.value)
                                }
                                className="w-full px-2 py-1.5 text-xs bg-slate-50 ring-1 ring-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:bg-white transition-colors"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/60 flex items-center justify-between">
                        <button
                          onClick={handleResetFilters}
                          disabled={!isFilterActive}
                          className="text-xs font-semibold text-rose-600 hover:text-rose-700 disabled:text-slate-400 disabled:cursor-not-allowed transition-colors">
                          Reset
                        </button>
                        <button
                          onClick={() => setFilterOpen(false)}
                          className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700 transition-colors">
                          Terapkan
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50/80 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 w-20">
                      Aksi
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      No. Transaksi
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Waktu Transaksi
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Pembayaran
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Total Belanja
                    </th>
                    <th className="px-6 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Dibuat oleh
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredTransactions.length === 0 ? (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-8 text-center text-slate-500">
                        {isFilterActive || searchTerm
                          ? "Tidak ada transaksi yang sesuai dengan filter"
                          : "Belum ada riwayat transaksi"}
                      </td>
                    </tr>
                  ) : (
                    filteredTransactions.map((transaction) => (
                      <tr
                        key={transaction.id}
                        className="hover:bg-slate-50/70 transition-colors">
                        <td className="px-6 py-4">
                          <button
                            onClick={() => setSelectedTransaction(transaction)}
                            className="w-8 h-8 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center justify-center transition-colors"
                            title="Detail">
                            <Info className="w-3.5 h-3.5" />
                          </button>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700 font-medium">
                          {transaction.transaction_number || transaction.id}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          {formatDate(transaction.created_at || transaction.order_date || customerData.last_transaction)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          {transaction.payment_method}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-900 font-semibold">
                          Rp {formatNumberWithSeparator(transaction.total_transaction || transaction.total)}
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-700">
                          {transaction.created_by}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination (Simplified for Detail Page) */}
            <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
              <span className="text-sm text-slate-600">
                Menampilkan {filteredTransactions.length} dari{" "}
                {transactions.length} transaksi
              </span>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1.5 bg-white ring-1 ring-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  Previous
                </button>
                <button className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium shadow-sm shadow-teal-600/20">
                  1
                </button>
                <button className="px-3 py-1.5 bg-white ring-1 ring-slate-200 rounded-lg text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  Next
                </button>
              </div>
            </div>
          </div>

          {/* Notes Section */}
          <CustomerNotes 
            customerId={id} 
            notes={customerData.customer_notes} 
            onNoteAdded={fetchCustomerData} 
            onNoteDeleted={fetchCustomerData} 
          />
        </main>
      </div>

      {selectedTransaction && (
        <CustomerTransactionDetailModal
          transaction={selectedTransaction}
          customerName={customerData.name}
          onClose={() => setSelectedTransaction(null)}
        />
      )}
    </div>
  );
}
